import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, TextField, Button, IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import UplaodForm from '../../../components/forms/UploadForm/UploadForm';
import './Documents.css';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewMTModal from '../../SupportCoordinator/Modals/ViewMTModal';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CircularProgress from '@mui/material/CircularProgress';
import { convertTime } from 'src/utils/time';

const Documents = (props) => {
    const [documentsDisplay, setDocumentsDisplay] = useState([]);
    const [showUpload, setShowUpload] = useState(false);
    const [showMTModal, setShowMTModal] = useState(false);
    const [selectedMT, setSelectedMT] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = async () => {
        let monthlyMonitoringList = [];
        try {
            const mtRecords = await axios.get(`${APIURL}/monthlymonitoring/customer/${props.cus_id}`);
            if (Array.isArray(mtRecords.data)) {
                monthlyMonitoringList = mtRecords.data.map((mt, idx) => {
                    return {
                        id: `mt-${mt.monthly_monitoring_id || idx}`,
                        uploaded_by: mt.employee_id || 'N/A',
                        uploaded: mt.created_on || mt.last_updated || '',
                        mt_type: mt.mt_type,
                        mt_period: moment(mt.mt_period, "MMM YYYY"),
                        data: mt.data,
                        monthly_monitoring_id: mt.monthly_monitoring_id,
                        created_on: mt.created_on,
                        last_updated: mt.last_updated,
                        tas_title: mt.tas_title,
                        tas_start_date: mt.tas_start_date,
                        tas_start_time: convertTime(mt.tas_start_time, mt.task_emp_timezone, moment.tz.guess()) ?? mt.tas_start_time,
                        tas_end_date: mt.tas_end_date,
                        tas_end_time: convertTime(mt.tas_end_time, mt.task_emp_timezone, moment.tz.guess()) ?? mt.tas_end_time,
                    };
                });
            }            
        } catch (err) {
            console.error('Error fetching monthly monitoring records:', err);
        }
        setDocumentsDisplay(monthlyMonitoringList.sort((a, b) => new Date(b.uploaded) - new Date(a.uploaded)));
        setIsLoading(false);
    };

    useEffect(() => {
        setIsLoading(true);
        fetchData();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!showUpload) {
            setIsLoading(true);
            fetchData();
        }
        // eslint-disable-next-line
    }, [showUpload]);

    const handleOpenMTModal = (item) => {
        setShowMTModal(true);
        setSelectedMT(item);
    };

    const handleCloseMTModal = () => {
        setShowMTModal(false);
        setSelectedMT(null);
    };


    const rows = documentsDisplay;

    const columns = [
        {
            field: 'view',
            headerName: 'MT',
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: '100%' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpenMTModal(params.row)}
                    >
                        View
                    </Button>
                </div>
            ),
        },
        {
            field: 'tas_title',
            headerName: 'Task Title',
            width: 250,
            renderCell: (params) => (
                <span style={{ width: '100%' }}>
                    {params.row?.tas_title || ''}
                </span>
            ),
        },
        {
            field: 'task_datetime',
            headerName: 'Task Start/End',
            width: 350,
            renderCell: (params) => {
                const { tas_start_date, tas_start_time, tas_end_date, tas_end_time } = params.row;
                let start = '';
                let end = '';
                if (tas_start_date && tas_start_time) {
                    let startMoment = moment(`${tas_start_date} ${tas_start_time}`);
                    if (!startMoment.isValid()) {
                        startMoment = moment(tas_start_date);
                    }
                    if (startMoment.isValid()) {
                        if (tas_start_time) {
                            const [h, m, s] = tas_start_time.split(':');
                            startMoment.set({
                                hour: parseInt(h, 10) || 0,
                                minute: parseInt(m, 10) || 0,
                                second: parseInt(s, 10) || 0,
                            });
                        }
                        start = startMoment.format('MM/DD/YYYY hh:mm A');
                    }
                } else if (tas_start_date) {
                    const startMoment = moment(tas_start_date);
                    if (startMoment.isValid()) start = startMoment.format('MM/DD/YYYY');
                }
                if (tas_end_date && tas_end_time) {
                    let endMoment = moment(`${tas_end_date} ${tas_end_time}`);
                    if (!endMoment.isValid()) {
                        endMoment = moment(tas_end_date);
                    }
                    if (endMoment.isValid()) {
                        if (tas_end_time) {
                            const [h, m, s] = tas_end_time.split(':');
                            endMoment.set({
                                hour: parseInt(h, 10) || 0,
                                minute: parseInt(m, 10) || 0,
                                second: parseInt(s, 10) || 0,
                            });
                        }
                        end = endMoment.format('MM/DD/YYYY hh:mm A');
                    }
                } else if (tas_end_date) {
                    const endMoment = moment(tas_end_date);
                    if (endMoment.isValid()) end = endMoment.format('MM/DD/YYYY');
                }
                if (!start && !end) return <span style={{ width: '100%' }}>-</span>;
                return (
                    <span style={{ width: '100%' }}>
                        {start && end ? `${start} - ${end}` : (start || end)}
                    </span>
                );
            },
        },
        {
            field: 'mt_type',
            headerName: 'Type',
            renderCell: (params) => (
                <span style={{ width: '100%' }}>
                    {params.row?.mt_type || ''}
                </span>
            ),
        },
        {
            field: 'mt_period',
            headerName: 'Date',
            width: 160,
            type: 'date',
            valueGetter: (value) => {
                return value.toDate()
            },
            valueFormatter: (value) => {
                return moment(value).format('MMM YYYY')
            }
        },
    ];

    return (
        <div className="DocumentsPage">
            <div className="tasks_info" style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '10px' }}>
                    <h5>MTS</h5>
                </div>                {isLoading ?
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </div> :
                    <div style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                        <DataGridPro
                            rows={rows}
                            columns={columns}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            checkboxSelection={false}
                            disableRowSelectionOnClick
                            getRowId={(row) => row.id}
                            sx={{ background: '#fff' }}
                            showToolbar />
                    </div>
                }
            </div>
            <Dialog open={showUpload} onClose={() => setShowUpload(false)}>
                <DialogActions>
                    <IconButton aria-label="close" onClick={() => setShowUpload(false)}><CloseIcon /></IconButton>
                </DialogActions>
                <DialogContent>
                    <UplaodForm
                        company_id={props.comp_id}
                        mode="add"
                        user={props.user}
                        customer_id={props.cus_id}
                        onSubmit={() => setShowUpload(false)}
                    />
                </DialogContent>
            </Dialog>
            {showMTModal && selectedMT && (
                <ViewMTModal
                    user={props.user}
                    show={showMTModal}
                    handleClose={handleCloseMTModal}
                    type={selectedMT.mt_type}
                    data={selectedMT.data ? JSON.parse(selectedMT.data) : {}}
                    mm_id={selectedMT.monthly_monitoring_id}
                    cus_name={props.cus_name}
                    checkoutEmpDate={selectedMT.created_on || selectedMT.last_updated}
                />
            )}
        </div>
    );
};

export default Documents;
